/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tagName } from '@ember-decorators/component';
import { inject as service } from '@ember/service';
import { or } from '@ember/object/computed';
import { dropTask } from 'ember-concurrency';
import { isNone } from '@ember/utils';
import { run } from '@ember/runloop';
import { importSync } from '@embroider/macros';
import EventBusService, { VWB_ACTION_UPDATED } from 'cio/services/event-bus';
import ApiService from 'cio/services/api';
import ApiErrorsService from 'cio/services/api-errors';
import ActionSubjectsService from 'cio/services/action-subjects';
import DS from 'ember-data';
import VisualWorkflowBuilderService from 'cio/services/visual-workflow-builder';
import ActionModel from 'cio/models/action';
import { computed } from '@ember/object';
import { MessagingChannelType } from 'cio/utils/setup-guide/messaging-channels';
import { ComponentLike } from '@glint/template';

interface EditActionSidebarMessageArgs {
  actionAbstract: any;
  isAddedOrMoving: boolean;
  onSelect: (action: ActionModel) => unknown;
  onDeselect: () => unknown;
  onDeleteStarted: () => unknown;
  onDeleteConfirmed: () => unknown;
  onSetupComplete: () => unknown;
  demoMode: boolean;
  hasReadOnlyPermission: boolean;
}

@tagName('')
export default class Message extends Component<EditActionSidebarMessageArgs> {
  @service api!: ApiService;
  @service apiErrors!: ApiErrorsService;
  @service actionSubjects!: ActionSubjectsService;
  @service eventBus!: EventBusService;
  @service store!: DS.Store;
  @service visualWorkflowBuilder!: VisualWorkflowBuilderService;

  get collections() {
    return this.store.peekAll('collection');
  }

  @or('saveMessage.isRunning', 'deleteMessage.isRunning', 'createSplit.isRunning') isSaving!: boolean;

  saveMessage = dropTask(async () => {
    try {
      await this.args.actionAbstract.saveTask.perform();
      this.visualWorkflowBuilder.redrawConnectingLines();
      this.eventBus.publish(VWB_ACTION_UPDATED, this.args.actionAbstract.action);
      this.args.onDeselect();
    } catch (e) {
      this.apiErrors.report(e);
      throw e;
    }
  });

  deleteMessage = dropTask(async () => {
    try {
      this.args.onDeleteConfirmed();
      await this.args.actionAbstract.deleteTask.perform();
      this.args.onDeselect();
    } catch (e) {
      this.apiErrors.report(e);
      throw e;
    }
  });

  createSplit = dropTask(async () => {
    try {
      let response = await this.args.actionAbstract.createSplitTask.perform();
      this.args.actionAbstract.action.belongsTo('campaign').value()?.parseTree();

      let { meta } = response;

      if (isNone(meta)) {
        return;
      }

      let action = this.store.peekRecord('action', meta.action_id);
      if (action == null) {
        return;
      }

      let parentAction = action.belongsTo('parentAction').value() as ActionModel | null;

      if (parentAction == null) {
        return;
      }

      this.args.onDeselect();
      // Allow the parent components to settle their properties first before
      // showing the parent split's edit sidebar.
      // We need to deselect first, because when you create a split, the
      // action remains the same, we just change its type. So if we were to
      // not deselect, the action under the actionAbstract would change,
      // which will lead to invalid states.
      run(() => {
        if (parentAction == null) {
          return;
        }
        this.args.onSelect(parentAction);
      });

      this.visualWorkflowBuilder.redrawConnectingLines();
    } catch (e) {
      this.apiErrors.report(e);
      throw e;
    }
  });

  @computed('args.actionAbstract.action.{type,info.requiresSetup}')
  get setupComponent() {
    if (!this.args.actionAbstract.action.info.requiresSetup) {
      return null;
    }

    let channelName: MessagingChannelType = this.args.actionAbstract.action.type.replace(/_action$/, '');
    let module = importSync(`../../../setup-guide/channels/messaging/${channelName.replace('_', '-')}`) as {
      default: ComponentLike<any>;
    };

    return module.default;
  }
}
